import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format, parseISO } from "date-fns";
import { mdy, mdyDow } from "@/lib/dateFmt";
import { Plus, X, AlertTriangle, Sparkles, Users2, Loader2, Send, Info, Smartphone, Mail, MessageSquare, Copy, Undo2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { colorForShift } from "@/lib/shifts";

/**
 * Open Shift Manager (v2) — click any Open Shift cell on the grid.
 * Section A — "Shifts needed today": every required slot rendered as a tile,
 *   showing who's on it or "OPEN". Manager can × any tile to drop the
 *   requirement (with a warning modal if it removes a covered slot) or add
 *   another shift from a picker at the bottom of the tile grid.
 * Section B — "Send offer": pick an open shift → list eligible employees
 *   split into Non-OT (default checked) and OT (unchecked + warning). Email
 *   channel is always on; Text is greyed until the manager's Apple Shortcut
 *   is configured. "Preview & send" opens a review modal before firing.
 */
export default function OpenShiftManagerDialog({
  open, onClose, facilityId, facilityLabel, dateIso, codes,
  currentUserName, onMutated,
}) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [addCode, setAddCode]     = useState("");
  const [confirmRemove, setConfirmRemove] = useState(null); // { code, index, assigned_employee }
  const [shortcutSettings, setShortcutSettings] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [sentOffers, setSentOffers] = useState([]);

  // Offer builder state
  const [offerCode, setOfferCode]         = useState(null);
  const [candidates, setCandidates]       = useState({ loading: false, list: [] });
  const [selected, setSelected]           = useState(new Set());
  const [channelEmail, setChannelEmail]   = useState(true);
  const [channelText, setChannelText]     = useState(false);
  const [previewOpen, setPreviewOpen]     = useState(false);
  const [sending, setSending]             = useState(false);

  const codeMap = useMemo(() => new Map((codes || []).map(c => [c.code, c])), [codes]);
  // iOS detection — the "Send via Shortcut" text channel only works from an
  // actual iPhone/iPad because Shortcuts.app is Apple-only. On desktop we
  // hide the option entirely to keep the UI clean; on iOS we still gate it
  // behind the shortcut being configured.
  const isIOS = useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = navigator.userAgent || "";
    // iPad on iOS 13+ reports "MacIntel" — check for touch as a signal.
    return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && navigator.maxTouchPoints > 1);
  }, []);
  const textReady = isIOS && !!shortcutSettings?.icloud_share_url && !!shortcutSettings?.shortcut_name;

  const load = async () => {
    if (!open || !dateIso || !facilityId) return;
    setLoading(true);
    try {
      const [main, offers] = await Promise.all([
        api.get(`/open-shift-manager?facility_id=${facilityId}&date=${dateIso}`),
        api.get(`/open-shift-manager/day-offers?facility_id=${facilityId}&date=${dateIso}`),
      ]);
      setData(main.data);
      setSentOffers(offers.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load"); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    setOfferCode(null); setCandidates({ loading: false, list: [] }); setSelected(new Set());
    if (open) {
      api.get(`/app-settings/bcc-shortcut`).then(r => setShortcutSettings(r.data)).catch(() => setShortcutSettings(null));
    }
    /* eslint-disable-next-line */
  }, [open, dateIso, facilityId]);

  useEffect(() => {
    // Auto-preview text channel un-selection whenever shortcut isn't set up
    if (!textReady) setChannelText(false);
  }, [textReady]);

  const mutate = async (kind, code) => {
    try {
      await api.post(`/open-shift-manager/${kind}`, { facility_id: facilityId, date: dateIso, code });
      toast.success(kind === "add" ? `Added ${code}` : `Removed ${code}`);
      load();
      onMutated && onMutated();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };

  const dismissCoverage = async (slot) => {
    if (slot.assigned_coverer) {
      toast.error("Clear the assigned coverer first (click the Cvg tile → Clear coverage)");
      return;
    }
    if (!window.confirm(`Drop this ${slot.code} coverage requirement for the day? It will disappear from the open-shifts row. You can restore it from the same menu.`)) return;
    try {
      await api.post(`/open-shift-manager/dismiss-coverage`, { assignment_id: slot.assignment_id });
      toast.success(`Coverage dropped for ${slot.code}`);
      load();
      onMutated && onMutated();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };

  const loadCandidates = async (code) => {
    setOfferCode(code);
    setCandidates({ loading: true, list: [] });
    setSelected(new Set());
    try {
      const { data } = await api.get(`/pickup-candidates?facility_id=${facilityId}&date=${dateIso}&shift_code=${encodeURIComponent(code)}`);
      // Pre-select every non-OT candidate
      setCandidates({ loading: false, list: data || [] });
      setSelected(new Set((data || []).filter(c => c.ot_warnings.length === 0).map(c => c.id)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
      setCandidates({ loading: false, list: [] });
    }
  };

  const toggle = (id) => setSelected((prev) => {
    const nxt = new Set(prev);
    if (nxt.has(id)) nxt.delete(id); else nxt.add(id);
    return nxt;
  });
  const bulkToggle = (ids, on) => setSelected((prev) => {
    const nxt = new Set(prev);
    if (on) ids.forEach(i => nxt.add(i)); else ids.forEach(i => nxt.delete(i));
    return nxt;
  });

  const nonOtList = candidates.list.filter(c => c.ot_warnings.length === 0);
  const otList    = candidates.list.filter(c => c.ot_warnings.length > 0);

  const buildOfferMsg = (code) => {
    const m = codeMap.get(code) || {};
    const timeRange = (m.start && m.end) ? `${m.start} – ${m.end}` : "";
    const dOn = mdyDow(dateIso);
    return `Hello [First Name], Open shift: ${m.label || code}${timeRange ? ` (${timeRange})` : ""} date: ${dOn} — are you available to help? — ${currentUserName || "Manager"}`;
  };

  const sendOffer = async () => {
    if (!offerCode) return;
    const chosen = candidates.list.filter(x => selected.has(x.id));
    if (chosen.length === 0) { toast.error("Tick at least one recipient"); return; }
    if (!channelEmail && !channelText) { toast.error("Pick at least one channel (email or text)"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/open-shift-manager/offer`, {
        facility_id: facilityId, date: dateIso, shift_code: offerCode,
        contact_ids: chosen.map(x => x.id),
        message: buildOfferMsg(offerCode),
        send_email: channelEmail, send_text: channelText,
      });
      const parts = [];
      if (channelEmail) parts.push(`${data.emails_sent} email${data.emails_sent === 1 ? "" : "s"} sent`);
      if (data.emails_skipped) parts.push(`${data.emails_skipped} skipped (no email)`);
      toast.success(`Offer sent — ${parts.join(" · ")}`);
      // If Text channel is on AND Shortcut set up, launch iOS shortcut too.
      // Use x-callback-url so iOS returns focus to Safari after the run and
      // pass the JSON as `text` (with `input=text`) so "Get Dictionary from
      // Shortcut Input" can consume it directly.
      if (channelText && textReady && (data.contacts || []).length > 0) {
        const shortcutName = data.shortcut_name || "OpenShiftOffer";
        const shortcutPayload = { contacts: data.contacts, message: buildOfferMsg(offerCode) };
        const jsonStr = JSON.stringify(shortcutPayload);
        const shortcutUrl = `shortcuts://x-callback-url/run-shortcut?name=${encodeURIComponent(shortcutName)}&input=text&text=${encodeURIComponent(jsonStr)}`;
        window.location.href = shortcutUrl;
      }
      setPreviewOpen(false);
      setOfferCode(null); setCandidates({ loading: false, list: [] }); setSelected(new Set());
      onMutated && onMutated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to send offer");
    }
    setSending(false);
  };

  if (!open) return null;
  const totalRequired = Object.values(data?.template_required || {}).reduce((a,b)=>a+b,0);
  const totalAssigned = Object.values(data?.template_assigned || {}).reduce((a,b)=>a+b,0);
  const openCount = (data?.open_slots || []).length;

  return (
    <TooltipProvider>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="open-shift-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users2 size={18} className="text-blue-600" />
            Day Coverage — {dateIso && mdyDow(dateIso)}
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-0.5">
            {facilityLabel} · Add or remove shift needs for this specific date. Master schedule is untouched unless you edit it there.
          </div>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-8 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={18} /></div>
        ) : (
          <>
            {/* SUMMARY STRIP */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center gap-3 flex-wrap text-xs text-slate-600">
              <span><b className="tabular-nums text-slate-900">{totalRequired}</b> needed</span>
              <span className="text-slate-300">·</span>
              <span><b className="tabular-nums text-emerald-700">{totalAssigned}</b> covered</span>
              <span className="text-slate-300">·</span>
              <span><b className="tabular-nums text-amber-700">{openCount}</b> open</span>
              <div className="ml-auto flex items-center gap-1.5">
                <select
                  value={addCode}
                  onChange={(e) => setAddCode(e.target.value)}
                  data-testid="add-open-slot-select"
                  className="rounded-md border border-slate-300 px-2 py-1 text-sm bg-white max-w-[220px] truncate"
                >
                  <option value="">Add a shift…</option>
                  {(codes || []).filter(c => c.type === "work").map((c) => (
                    <option key={c.code} value={c.code}>{c.code} · {c.label}{c.start && c.end ? ` (${c.start}–${c.end})` : ""}</option>
                  ))}
                </select>
                <Button size="sm"
                  onClick={() => { if (addCode) { mutate("add", addCode); setAddCode(""); } }}
                  disabled={!addCode}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="add-open-slot-btn"
                >
                  <Plus size={13} className="mr-1" /> Add shift
                </Button>
              </div>
            </div>

            {/* SECTION A — All shifts needed today (tiles) */}
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Shifts needed today</div>
                <div className="text-[10px] text-slate-400">(× to drop the requirement for {mdy(dateIso)})</div>
              </div>
              {(data.all_slots || []).length === 0 ? (
                <div className="py-6 text-center text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl">
                  No shifts scheduled for this date. Use "Add shift" above to build coverage.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="day-tiles">
                  {(data.all_slots || []).map((s, idx) => {
                    const clr = colorForShift(s.code, codeMap);
                    const covered = !!s.assigned_employee;
                    return (
                      <div key={`${s.code}-${idx}`}
                        className={`relative rounded-xl border p-2.5 ${covered ? "bg-white border-slate-200" : "bg-amber-50 border-amber-200"}`}
                        data-testid={`day-tile-${s.code}-${idx}`}
                      >
                        <button
                          onClick={() => {
                            if (covered) setConfirmRemove({ code: s.code, index: idx, assigned_employee: s.assigned_employee });
                            else mutate("remove", s.code);
                          }}
                          data-testid={`remove-tile-${s.code}-${idx}`}
                          title="Remove this requirement for the day"
                          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-slate-100 hover:bg-rose-500 hover:text-white text-slate-500 flex items-center justify-center transition-colors"
                        >
                          <X size={13} />
                        </button>
                        <div className={`inline-flex items-center rounded-md border px-2 py-0.5 text-sm font-bold ${clr?.bg} ${clr?.text} ${clr?.border}`}>{s.code}</div>
                        <div className="text-[11px] mt-1 text-slate-700 font-medium truncate">{s.label}</div>
                        <div className="text-[10px] text-slate-500 tabular-nums">
                          {s.start && s.end ? `${s.start}–${s.end}` : "—"}{s.hours ? ` · ${s.hours}h` : ""}
                        </div>
                        {s.department && (
                          <div className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-slate-500">
                            {s.department_color && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.department_color }} />}
                            {s.department}
                          </div>
                        )}
                        <div className="border-t border-slate-100 mt-2 pt-1.5 flex items-center gap-1.5 min-h-[24px]">
                          {covered ? (
                            <>
                              <Avatar className="w-5 h-5 shrink-0">
                                <AvatarImage src={s.assigned_employee.avatar} alt={s.assigned_employee.name} />
                                <AvatarFallback className="text-[9px]">{(s.assigned_employee.name || "??").split(" ").map(x => x[0]).slice(0,2).join("")}</AvatarFallback>
                              </Avatar>
                              <div className="text-[11px] text-slate-700 truncate">{s.assigned_employee.name}</div>
                            </>
                          ) : (
                            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 flex items-center gap-1">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" /> OPEN
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* SECTION A.4 — Coverage requirements (from modified shifts) */}
            {(data.coverage_slots || []).length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Coverage needed today</div>
                  <div className="text-[10px] text-slate-400">(shifts cut short by a modification — × to drop the coverage requirement)</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="coverage-tiles">
                  {(data.coverage_slots || []).map((s, idx) => {
                    const clr = colorForShift(s.code, codeMap);
                    const covered = !!s.assigned_coverer;
                    return (
                      <div key={`cov-${s.assignment_id}-${idx}`}
                        className={`relative rounded-xl border p-2.5 ${covered ? "bg-white border-pink-200" : "bg-pink-50 border-pink-200"}`}
                        data-testid={`coverage-tile-${s.assignment_id}-${idx}`}
                      >
                        <button
                          onClick={() => dismissCoverage(s)}
                          data-testid={`dismiss-coverage-${s.assignment_id}-${idx}`}
                          title={covered
                            ? "A coverer is assigned — clear them first from the Cvg tile on the schedule"
                            : "Drop this coverage requirement for the day"}
                          disabled={covered}
                          className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            covered
                              ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                              : "bg-slate-100 hover:bg-rose-500 hover:text-white text-slate-500"
                          }`}
                        >
                          <X size={13} />
                        </button>
                        <div className={`inline-flex items-center rounded-md border px-2 py-0.5 text-sm font-bold ${clr?.bg} ${clr?.text} ${clr?.border}`}>
                          {s.code}<span className="text-[10px] font-black tracking-widest ml-0.5">Cvg</span>
                        </div>
                        <div className="text-[11px] mt-1 text-slate-700 font-medium truncate">
                          {s.source_employee_name ? `From ${s.source_employee_name}` : "Coverage gap"}
                        </div>
                        <div className="text-[10px] text-slate-500 tabular-nums">
                          {s.gap_start && s.gap_end ? `${s.gap_start}–${s.gap_end}` : "—"}{s.gap_hours ? ` · ${s.gap_hours}h` : ""}
                          {s.partial_from_stack && <span className="ml-1 text-slate-400">(partial)</span>}
                        </div>
                        <div className="border-t border-pink-100 mt-2 pt-1.5 flex items-center gap-1.5 min-h-[24px]">
                          {covered ? (
                            <div className="text-[11px] text-emerald-700 font-semibold truncate">
                              ✓ {s.assigned_coverer.name}
                            </div>
                          ) : (
                            <div className="text-[10px] uppercase tracking-widest font-bold text-pink-700 flex items-center gap-1">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-pink-500" /> NEEDS COVER
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {sentOffers.length > 0 && (
              <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="sent-offers-panel">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Sent offers · {dateIso && mdy(dateIso)}</div>
                  <span className="text-[10px] text-slate-400 tabular-nums ml-auto">{sentOffers.length}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {sentOffers.map((o) => {
                    const clr = colorForShift(o.shift_code, codeMap);
                    const pending = (o.recipients || []).filter(r => r.status === "pending").length;
                    const accepted = (o.recipients || []).find(r => r.status === "accepted");
                    const declined = (o.recipients || []).filter(r => r.status === "denied").length;
                    const tooLate  = (o.recipients || []).filter(r => r.status === "too_late").length;
                    const canCancel = o.status === "open";
                    const doCancel = async () => {
                      if (!window.confirm("Cancel this offer? Every pending recipient will see 'shift no longer available'.")) return;
                      try {
                        await api.delete(`/open-shift-manager/day-offers/${o.offer_id}`);
                        toast.success("Offer cancelled");
                        await load();
                        onMutated && onMutated();
                      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to cancel"); }
                    };
                    return (
                      <div key={o.offer_id} className="p-2.5" data-testid={`sent-offer-${o.offer_id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-xs font-bold ${clr?.bg} ${clr?.text} ${clr?.border}`}>{o.shift_code}</div>
                          <span className="text-[11px] text-slate-600">
                            Sent {o.sent_at ? new Date(o.sent_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : ""} by <b>{o.sent_by_name}</b>
                          </span>
                          <span className="ml-auto flex items-center gap-1.5 flex-wrap">
                            {o.status === "filled" && accepted && (
                              <span className="rounded-md bg-emerald-100 border border-emerald-300 text-emerald-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                                Filled by {accepted.name}
                              </span>
                            )}
                            {o.status === "cancelled" && (
                              <span className="rounded-md bg-slate-200 border border-slate-300 text-slate-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                                Cancelled
                              </span>
                            )}
                            {o.status === "open" && pending > 0 && (
                              <span className="rounded-md bg-amber-100 border border-amber-300 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest tabular-nums">
                                {pending} pending
                              </span>
                            )}
                            {declined > 0 && (
                              <span className="rounded-md bg-slate-100 border border-slate-300 text-slate-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest tabular-nums">
                                {declined} declined
                              </span>
                            )}
                            {tooLate > 0 && (
                              <span className="rounded-md bg-slate-100 border border-slate-300 text-slate-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest tabular-nums">
                                {tooLate} late
                              </span>
                            )}
                            {canCancel && (
                              <button
                                type="button"
                                onClick={doCancel}
                                data-testid={`cancel-offer-${o.offer_id}`}
                                className="w-6 h-6 rounded-full bg-slate-100 hover:bg-rose-500 hover:text-white text-slate-500 flex items-center justify-center transition-colors"
                                title="Cancel offer for everyone"
                              >
                                <X size={13} />
                              </button>
                            )}
                          </span>
                        </div>
                        <div className="mt-1.5 grid sm:grid-cols-2 gap-1.5">
                          {(o.recipients || []).map((r) => (
                            <div key={r.id} className={`flex items-center gap-2 rounded border px-2 py-1 text-[11px] ${
                              r.status === "accepted" ? "bg-emerald-50 border-emerald-300 text-emerald-900" :
                              r.status === "denied"   ? "bg-slate-50 border-slate-200 text-slate-500" :
                              r.status === "too_late" ? "bg-amber-50 border-amber-200 text-amber-800" :
                                                         "bg-white border-slate-200 text-slate-700"
                            }`}>
                              <span className="truncate flex-1">{r.name}</span>
                              <span className="uppercase tracking-widest font-semibold text-[9px]">
                                {r.status === "accepted" ? "✓ Accepted"
                                : r.status === "denied"   ? "Declined"
                                : r.status === "too_late" ? "Late · already filled"
                                                          : "Pending"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SECTION B — Send offer for open shifts */}
            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/40 p-3">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                  <Sparkles size={12} className="text-blue-600" /> Send offer for open shift
                </div>
                <button type="button" onClick={() => setSetupOpen(true)}
                  className="text-[10px] text-slate-500 hover:text-blue-600 inline-flex items-center gap-1 uppercase tracking-widest"
                  data-testid="bcc-setup-open">
                  <Smartphone size={11} /> Shortcut setup
                </button>
              </div>

              {(data.open_slots || []).length === 0 ? (
                <div className="py-4 text-center text-xs text-slate-500 border border-dashed border-slate-200 rounded-lg bg-white">
                  Nothing open right now — every shift needed for this date is covered.
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-1.5 flex-wrap mb-3">
                    <span className="text-[11px] text-slate-500 mr-1">Which open shift?</span>
                    {(data.open_slots || []).map((s, idx) => {
                      const active = offerCode === s.code;
                      const clr = colorForShift(s.code, codeMap);
                      return (
                        <button key={`${s.code}-${idx}`}
                          onClick={() => loadCandidates(s.code)}
                          data-testid={`pick-open-${s.code}-${idx}`}
                          className={`rounded-md border px-2 py-1 text-xs font-semibold flex items-center gap-1.5 transition-colors ${active ? "ring-2 ring-blue-500 ring-offset-1" : ""} ${clr?.bg} ${clr?.text} ${clr?.border}`}
                        >
                          <span>{s.code}</span>
                          <span className="text-[10px] font-normal opacity-80 tabular-nums">{s.start && s.end ? `${s.start}–${s.end}` : `${s.hours}h`}</span>
                        </button>
                      );
                    })}
                  </div>

                  {offerCode && candidates.loading && (
                    <div className="py-6 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={18} /></div>
                  )}

                  {offerCode && !candidates.loading && candidates.list.length === 0 && (
                    <div className="py-4 text-center text-xs text-slate-500 bg-white rounded-lg border border-dashed border-slate-200">
                      Nobody at {facilityLabel} is off this day.
                    </div>
                  )}

                  {offerCode && !candidates.loading && candidates.list.length > 0 && (
                    <>
                      <CandidateGroup
                        title={<>Available <span className="text-emerald-700 normal-case">· no OT concerns</span></>}
                        emptyText="No one is off with clean hours."
                        list={nonOtList}
                        selected={selected}
                        onToggle={toggle}
                        onBulk={(on) => bulkToggle(nonOtList.map(x => x.id), on)}
                        variant="ok"
                      />
                      {otList.length > 0 && (
                        <CandidateGroup
                          title={<>Would break OT rules <span className="text-amber-700 normal-case">· pick carefully</span></>}
                          emptyText=""
                          list={otList}
                          selected={selected}
                          onToggle={toggle}
                          onBulk={(on) => bulkToggle(otList.map(x => x.id), on)}
                          variant="warn"
                        />
                      )}

                      {/* Channel picker + preview button */}
                      <div className="mt-3 flex items-center gap-3 flex-wrap p-2 rounded-lg bg-white border border-slate-200">
                        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="channel-email">
                          <Checkbox checked={channelEmail} onCheckedChange={(v) => setChannelEmail(!!v)} />
                          <Mail size={14} className="text-slate-500" /> Email
                        </label>
                        {isIOS && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <label className={`flex items-center gap-2 text-sm ${textReady ? "cursor-pointer" : "cursor-not-allowed opacity-40"}`} data-testid="channel-text">
                                <Checkbox checked={channelText && textReady} disabled={!textReady}
                                  onCheckedChange={(v) => textReady && setChannelText(!!v)} />
                                <MessageSquare size={14} className="text-slate-500" /> Text (iOS Shortcut)
                              </label>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs max-w-[240px]">
                              {textReady ? "Fires the saved Apple Shortcut and sends a text to every selected recipient's phone number on file (from Employees → Phone). Your iPhone Contacts don't have to match."
                                         : "Set up your Apple Shortcut first — tap 'Shortcut setup' above."}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {!isIOS && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-[11px] text-slate-400 flex items-center gap-1 cursor-help" data-testid="channel-text-desktop-note">
                                <MessageSquare size={12} /> Text only on iPhone
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs max-w-[240px]">
                              The Text channel launches an Apple Shortcut, which only exists on iOS. Open this app on your iPhone to send group texts. Email works everywhere.
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <div className="ml-auto text-[11px] text-slate-500 tabular-nums">
                          {selected.size} selected
                        </div>
                        <Button
                          size="sm"
                          onClick={() => setPreviewOpen(true)}
                          disabled={selected.size === 0 || (!channelEmail && !channelText)}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="preview-offer-btn"
                        >
                          Preview & send
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* Remove-covered-shift confirmation */}
        <Dialog open={!!confirmRemove} onOpenChange={(v) => !v && setConfirmRemove(null)}>
          <DialogContent className="max-w-md" data-testid="confirm-remove-tile">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-500" /> This shift is currently assigned
              </DialogTitle>
            </DialogHeader>
            {confirmRemove?.assigned_employee && (
              <div className="text-sm text-slate-600 space-y-2">
                <div>
                  <b className="text-slate-900">{confirmRemove.assigned_employee.name}</b> is currently assigned <b>{confirmRemove.code}</b> on {dateIso && mdy(dateIso)}.
                </div>
                <div className="text-[13px] rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-amber-900">
                  Removing this shift only drops it from today's needed-count. The employee's assignment stays as-is on the schedule.
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setConfirmRemove(null)}>Cancel</Button>
              <Button
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="confirm-remove-tile-btn"
                onClick={() => { mutate("remove", confirmRemove.code); setConfirmRemove(null); }}
              >
                Yes, drop requirement
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview & send dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-lg" data-testid="offer-preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send size={16} className="text-blue-600" /> Review offer
              </DialogTitle>
            </DialogHeader>
            {offerCode && (
              <>
                <div className="text-[11px] text-slate-500 mb-1">Message preview</div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-sm text-slate-700 whitespace-pre-wrap">
                  {buildOfferMsg(offerCode)}
                </div>
                <div className="text-[11px] text-slate-500 mt-3 flex items-center gap-2">
                  <span>Sending to <b className="tabular-nums">{selected.size}</b> recipient{selected.size === 1 ? "" : "s"} via {channelEmail && "Email"}{channelEmail && channelText && " + "}{channelText && "Text"}.</span>
                  <button className="ml-auto text-[11px] text-slate-500 hover:text-blue-600 inline-flex items-center gap-1"
                    onClick={() => { navigator.clipboard?.writeText(buildOfferMsg(offerCode)); toast.success("Message copied"); }}>
                    <Copy size={12} /> copy
                  </button>
                </div>
                <div className="mt-3 max-h-[240px] overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
                  {candidates.list.filter(c => selected.has(c.id)).map((c) => {
                    const otBad = c.ot_warnings.length > 0;
                    return (
                      <div key={c.id} className="flex items-center gap-2 p-2 text-sm">
                        <Avatar className="w-6 h-6 shrink-0">
                          <AvatarImage src={c.avatar} />
                          <AvatarFallback className="text-[9px]">{(c.name || "??").split(" ").map(x=>x[0]).slice(0,2).join("")}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="truncate">{c.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {channelEmail && c.email ? c.email : channelEmail ? <span className="italic text-slate-400">no email on file</span> : c.phone || <span className="italic text-slate-400">no phone</span>}
                          </div>
                        </div>
                        {otBad && (
                          <span className="text-[9px] uppercase tracking-widest text-amber-700 font-bold rounded bg-amber-100 border border-amber-200 px-1.5 py-[1px]">
                            OT risk
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between items-center pt-3">
                  <Button variant="ghost" size="sm" onClick={() => setPreviewOpen(false)}>
                    <ArrowLeft size={13} className="mr-1" /> Back to edit
                  </Button>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={sendOffer}
                    disabled={sending}
                    data-testid="offer-send-btn"
                  >
                    {sending ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Sending…</> : <><Send size={13} className="mr-1" /> Send offer</>}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <BccShortcutSetupDialog
          open={setupOpen}
          onClose={() => setSetupOpen(false)}
          settings={shortcutSettings}
          onSaved={(s) => setShortcutSettings(s)}
        />
      </DialogContent>
    </Dialog>
    </TooltipProvider>
  );
}

function CandidateGroup({ title, emptyText, list, selected, onToggle, onBulk, variant }) {
  if (list.length === 0 && !emptyText) return null;
  const allSelected = list.length > 0 && list.every(x => selected.has(x.id));
  const anySelected = list.some(x => selected.has(x.id));
  const wrap = variant === "warn"
    ? "border-amber-200 bg-amber-50/60"
    : "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border ${wrap} mt-2 overflow-hidden`}>
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 bg-white/70">
        <Checkbox
          checked={allSelected ? true : anySelected ? "indeterminate" : false}
          onCheckedChange={(v) => onBulk(v === true)}
        />
        <div className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold">{title}</div>
        <div className="ml-auto text-[10px] text-slate-400 tabular-nums">{list.length}</div>
      </div>
      {list.length === 0 ? (
        <div className="p-2 text-[11px] text-slate-500 italic">{emptyText}</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-1.5 p-2">
          {list.map((p) => {
            const otBad   = p.ot_warnings.length > 0;
            const checked = selected.has(p.id);
            return (
              <label key={p.id}
                className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${checked ? "border-blue-400 bg-blue-50/40" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                data-testid={`candidate-${p.id}`}
              >
                <Checkbox checked={checked} onCheckedChange={() => onToggle(p.id)} />
                <Avatar className="w-7 h-7 shrink-0">
                  <AvatarImage src={p.avatar} alt={p.name} />
                  <AvatarFallback className="text-[10px]">{(p.name || "??").split(" ").map(x => x[0]).slice(0,2).join("")}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  {otBad ? (
                    <div className="text-[10px] text-amber-800 leading-tight mt-0.5">
                      ⚠ {p.ot_warnings[0]}{p.ot_warnings.length > 1 && ` (+${p.ot_warnings.length - 1} more)`}
                    </div>
                  ) : (
                    <div className="text-[10px] text-emerald-700 mt-0.5">✓ Clean hours</div>
                  )}
                  {p.email && <div className="text-[10px] text-slate-400 truncate">{p.email}</div>}
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BccShortcutSetupDialog({ open, onClose, settings, onSaved }) {
  const [name, setName] = useState(settings?.shortcut_name || "OpenShiftOffer");
  const [icloud, setIcloud] = useState(settings?.icloud_share_url || "");
  const [saving, setSaving] = useState(false);
  const [recipeOpen, setRecipeOpen] = useState(false);
  useEffect(() => {
    if (open) {
      setName(settings?.shortcut_name || "OpenShiftOffer");
      setIcloud(settings?.icloud_share_url || "");
    }
  }, [open, settings]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/app-settings/bcc-shortcut`, { shortcut_name: name, icloud_share_url: icloud });
      onSaved && onSaved(data);
      toast.success("Shortcut settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    setSaving(false);
  };

  const RECIPE_STEPS = [
    'Get Dictionary from Input   ← pick "Shortcut Input" in the dropdown (NOT Clipboard / Current App / Date / Device)',
    'Get Dictionary Value → key "contacts" → set variable "contacts"',
    'Get Dictionary Value → key "message" → set variable "template"',
    'Repeat with Each item in "contacts":',
    '  → Get Dictionary Value → key "first_name" → set variable "fn"',
    '  → Get Dictionary Value → key "phone" → set variable "phone"',
    '  → Replace Text: find "[First Name]" in "template" · replace with "fn" → set variable "body"',
    '  → Send Message "body" to "phone"   (native iOS Messages)',
    'End Repeat',
    'Show Notification: "Open-shift offer sent"',
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="bcc-shortcut-setup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Smartphone size={16} className="text-fuchsia-600" /> Open-Shift Text Shortcut Setup</DialogTitle>
        </DialogHeader>
        <div className="text-[12px] text-slate-700 space-y-3">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
            <div className="font-semibold text-blue-900 flex items-center gap-1.5">
              <Info size={13} /> Optional — the Text channel stays greyed out until this is set up
            </div>
            <div className="text-[11px] text-blue-800 mt-1 leading-snug">
              Emails work without this. To also fire group texts via native iOS Messages when you send an offer, build the Shortcut once on your iPhone, share its iCloud link, and paste it below.
            </div>
            <div className="text-[11px] text-blue-900 mt-2 leading-snug border-t border-blue-200 pt-2">
              <b>Phone numbers come from this site, not your iPhone Contacts.</b> The Shortcut receives each employee's <code className="bg-white/60 rounded px-1">phone</code> field (from Employees → Edit → Phone) and passes it straight to iOS Messages. Names / matching contacts on your iPhone don't matter — Messages will happily text any valid number. Make sure every employee has a phone on file first.
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50">
            <div className="text-[10px] uppercase tracking-widest font-black text-slate-500">Install the Shortcut on this iPhone</div>
            {icloud ? (
              <>
                <a href={icloud} target="_blank" rel="noopener"
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-fuchsia-600 text-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest hover:bg-fuchsia-700"
                  data-testid="bcc-icloud-install-link">
                  <Send size={13} /> Install "{name}" Shortcut
                </a>
              </>
            ) : (
              <div className="text-[11px] text-amber-900 mt-1 rounded-md border border-amber-200 bg-amber-50 p-2 leading-snug">
                No install link yet. Build the Shortcut once on your iPhone (recipe below), tap <b>Share → Copy iCloud Link</b>, and paste that URL in Admin settings below.
              </div>
            )}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => setRecipeOpen((s) => !s)} data-testid="bcc-toggle-recipe">
                {recipeOpen ? "Hide" : "Show"} Shortcut recipe
              </Button>
              <Button size="sm" variant="outline"
                onClick={() => { navigator.clipboard?.writeText(RECIPE_STEPS.join("\n")); toast.success("Recipe copied"); }}
                data-testid="bcc-copy-recipe">
                <Copy size={13} className="mr-1" /> Copy recipe
              </Button>
            </div>
            {recipeOpen && (
              <ol className="mt-2 list-decimal ml-5 space-y-0.5 text-[11px] text-slate-600 leading-snug bg-white p-2 rounded border border-slate-200">
                {RECIPE_STEPS.map((step, i) => (
                  <li key={i} className="whitespace-pre-wrap">{step}</li>
                ))}
              </ol>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 p-3">
            <div className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1">Admin settings</div>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-700">Shortcut name (must match what you saved on iPhone — case sensitive)</span>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} data-testid="bcc-name-input" className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
            </label>
            <label className="block mt-2">
              <span className="text-[11px] font-medium text-slate-700">iCloud share URL (paste after "Copy iCloud Link" from Shortcuts app)</span>
              <input type="url" value={icloud} onChange={(e) => setIcloud(e.target.value)} data-testid="bcc-icloud-input" placeholder="https://www.icloud.com/shortcuts/…" className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
            </label>
            <div className="flex justify-end gap-2 mt-2">
              <Button size="sm" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="bcc-setup-save">
                {saving ? "Saving…" : "Save settings"}
              </Button>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
